import { useEffect, useRef, useState } from 'react';
import BackendService from '../../services/BackendService';
import useAppContext from '../useAppContext/useAppContext';
import { useInterval } from 'react-use';
import { DEVICE_HEARTBEAT_TIMEOUT_MS } from '../../utils/consts';
import { trackDeviceConnectionState } from '../../logging/deviceConnectionDiagnostics';

/**
 * Freshest heartbeat we have seen for a session, kept outside the hook.
 *
 * `sessions/currentState` serves every field from the database except `timestamp`, which
 * the backend reads from an in-process map (`sessionDeviceStatusReportTiming`). Production
 * runs up to 10 App Engine instances, and each one only knows about the status reports it
 * handled itself — so depending on which instance answers a poll, the stamp can be missing
 * (that instance never saw a report) or arbitrarily old (it last saw one minutes ago). The
 * headset is fine in both cases; the evidence is just spread unevenly over instances. On
 * live this shows up as the "last sync" time in the headset tooltip jumping backwards
 * between polls.
 *
 * That is why this cache is monotonic: it keeps the newest stamp any poll has produced,
 * and `isConnected` is derived from that instead of from the latest response. A real
 * disconnect still ages out — once the device goes quiet, no instance produces a fresher
 * stamp and the cached one crosses DEVICE_HEARTBEAT_TIMEOUT_MS.
 *
 * Holding the value here rather than in a ref also keeps the ~7 hook instances on the
 * practitioner page agreeing with each other instead of each flipping on its own
 * polling schedule.
 */
const lastDeviceHeartbeats = new Map<string, string>();

/**
 * Picks whichever of two heartbeat stamps is newer, tolerating absent or unparseable
 * values. Stamps are `toUTCString()` strings, so they have to be compared as dates —
 * comparing them as strings would be meaningless.
 */
const newerTimestamp = (a: string | undefined, b: string | undefined): string | undefined => {
  const aMs = a ? new Date(a).getTime() : NaN;
  const bMs = b ? new Date(b).getTime() : NaN;
  if (!Number.isFinite(bMs)) return a ?? b;
  if (!Number.isFinite(aMs)) return b;
  return aMs >= bMs ? a : b;
};

const rememberDeviceHeartbeat = (sessionKey: string, timestamp: string) => {
  // One session per tab in practice; drop anything else so this cannot grow unbounded.
  if (lastDeviceHeartbeats.size && !lastDeviceHeartbeats.has(sessionKey)) {
    lastDeviceHeartbeats.clear();
  }
  const next = newerTimestamp(lastDeviceHeartbeats.get(sessionKey), timestamp);
  if (next) {
    lastDeviceHeartbeats.set(sessionKey, next);
  }
};

const useCurrentSessionState = (delay = 5000) => {
  const { sessionId, treatmentId, hasDevice } = useAppContext();
  const [currentState, setCurrentState] = useState<any>({});
  const [initialState, setInitialState] = useState<any>(undefined);
  const consecutiveFetchFailuresRef = useRef(0);

  const sessionKey = `${sessionId}-${treatmentId}`;

  useInterval(() => {
    BackendService.getCurrentState(sessionId, treatmentId)
      .then((result: any) => {
        // `getCurrentState` swallows network and parse errors into null, and the backend
        // answers with the string "no session found" when the row is gone. Neither is a
        // state we can read, so count them as a failed poll and keep the previous one.
        if (!result || typeof result !== 'object') {
          consecutiveFetchFailuresRef.current += 1;
          return null;
        }
        consecutiveFetchFailuresRef.current = 0;

        if (result.timestamp) {
          rememberDeviceHeartbeat(sessionKey, result.timestamp);
        }

        if (!initialState) {
          setInitialState(result);
        } else {
          setCurrentState(result);
        }
      })
      .catch((e) => {
        consecutiveFetchFailuresRef.current += 1;
        console.error(e);
      });
  }, delay);

  const {
    mediaType,
    rotation,
    mediaProjection,
    displayMode,
    mediaStereoMode,
    playbackType,
    mediaState,
    loadProgress,
    errorMessage,
    token,
    generatedUUID,
    headsetWorn,
    wifiQuality,
    wifiSpeed,
    batteryHeadset,
    batteryController1,
    batteryController2,
    timestamp,
    serialNumber,
    version = '1.0.0',
    waitingForDeviceCheck: waitingForDeviceCheckRaw,
    isWaitingForDeviceCheck: isWaitingForDeviceCheckRaw,
  } = currentState;

  const stickyTimestamp = lastDeviceHeartbeats.get(sessionKey);
  // Judge freshness by the newest stamp we know of, not by the latest response: a poll
  // answered by a backend instance the device hasn't reported to lately carries a stamp
  // *older* than one we've already seen, and taking it at face value would read as a
  // disconnect on that single unlucky poll.
  const effectiveTimestamp = newerTimestamp(timestamp, stickyTimestamp);

  // Plain elapsed milliseconds. The heartbeat is serialised with `toUTCString()`, so it
  // always carries an explicit GMT offset and parses unambiguously.
  const ageMs = effectiveTimestamp ? Date.now() - new Date(effectiveTimestamp).getTime() : null;

  // A negative age means the heartbeat is stamped in the future, i.e. the practitioner's
  // clock disagrees with the server's. Tolerate that up to the same window rather than
  // reading it as freshness.
  const isConnected =
    ageMs !== null &&
    Number.isFinite(ageMs) &&
    ageMs >= -DEVICE_HEARTBEAT_TIMEOUT_MS &&
    ageMs < DEVICE_HEARTBEAT_TIMEOUT_MS;

  useEffect(() => {
    if (!hasDevice || !initialState) {
      return;
    }

    trackDeviceConnectionState(sessionKey, isConnected, {
      ageMs,
      timestampMissing: !timestamp,
      usedStickyFallback: !timestamp && !!stickyTimestamp,
      timestampRolledBack: !!timestamp && effectiveTimestamp !== timestamp,
      consecutiveFetchFailures: consecutiveFetchFailuresRef.current,
      serialNumber: serialNumber ?? null,
    });
    // Only the transition matters here; the rest is context captured at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, hasDevice, initialState, sessionKey]);

  // If it hasn't polled yet, empty return;
  if (!initialState) {
    return {};
  }

  const normalizeBoolean = (value: unknown): boolean | undefined => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
      if (value === 'true' || value === '1') return true;
      if (value === 'false' || value === '0') return false;
    }
    return undefined;
  };

  const waitingForDeviceCheck =
    normalizeBoolean(waitingForDeviceCheckRaw) ?? normalizeBoolean(isWaitingForDeviceCheckRaw) ?? false;

  // Ignore the initial state (first poll);
  if (JSON.stringify(initialState) == JSON.stringify(currentState)) {
    return {
      token: null,
      mediaState,
      generatedUUID,
      headsetWorn,
      wifiQuality,
      wifiSpeed,
      batteryHeadset,
      batteryController1,
      batteryController2,
      timestamp,
      isConnected,
      serialNumber,
      waitingForDeviceCheck,
      version,
    };
  }

  const tokenToReturn = initialState.token !== token ? token : null;

  if (hasDevice) {
    return {
      token: tokenToReturn,
      errorMessage,
      generatedUUID,
      headsetWorn,
      wifiQuality,
      wifiSpeed,
      batteryHeadset,
      batteryController1,
      batteryController2,
      timestamp,
      isConnected,
      serialNumber,
      waitingForDeviceCheck,
      mediaState,
      loadProgress,
      mediaType,
      rotation,
      mediaProjection,
      displayMode,
      mediaStereoMode,
      playbackType,
      version,
    };
  }

  return {
    mediaType,
    rotation,
    mediaProjection,
    displayMode,
    mediaStereoMode,
    playbackType,
    mediaState,
    loadProgress,
    errorMessage,
    headsetWorn,
    wifiQuality,
    wifiSpeed,
    batteryHeadset,
    batteryController1,
    batteryController2,
    isConnected,
    serialNumber,
    waitingForDeviceCheck,
    token: tokenToReturn,
    version,
  };
};

export default useCurrentSessionState;
